import { StyledProps } from '@front10';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import React from 'react';
import {
  getBusStations as getBusStationsDefaultImpl,
  getLocations as getLocationsDefaultImpl
} from '../../../_integrations/everymundo/busDirectory';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import Map from '../../controls/MapRC';
import Box from '../../elements/Box';
import Button from '../../elements/Button';
import Col from '../../elements/Col';
//-------------------------------------
// Basic components here
//-------------------------------------
import Header from '../../elements/Header';
import Row from '../../elements/Row';
import Text from '../../elements/Text';
import StationItem from '../../items/StationItem/StationItem';
import List from '../../lists/FareListTwoColumns';
import Disclaimer from '../../others/Disclaimer';
import Loading from '../../others/Loading';
import NoFaresBehavior from '../../others/NoFaresBehavior';
import NoResults from '../../others/NoResults';
import Skeleton from '../../others/Skeleton/SkeletonBusDirectoryMap';
import LoadMorePaginator from '../../pagination/LoadMorePaginator';
import withDeepMergeDefaultProps from '../../utils/WithDeepMergeDefaultProps';
import { useBusDirectory, ActionType } from '../_commons/busDirectoryHooks';
import { busStationsDefaultPrePopulation } from '../_commons/prepopulationDefaultProps/busStationsDefaultPrePopulation';
import { AutoComplete } from './Autocomplete';
import flavors from './flavors';
import { PaginationDescription } from './PaginationDescription';
import { StationsInterlink } from './StationsInterlink';
import { TextProps } from '../../elements/Text/Text';

interface UIDisplay {
  showDisclaimer: boolean;
  showSkeletonScreen: boolean;
  showTitle: boolean;
  showBookNow: boolean;
  showOrigin: boolean;
  showDestination: boolean;
  showLoadMorePaginator: boolean;
  showInterlinkingSection: boolean;
  showPaginationResultsLabel: boolean;
  showFilters: boolean;
}

type Components = Partial<{
  title: StyledProps;
  interlinkingSettings: StyledProps;
  list: StyledProps<$TSFixMe>;
  skeletonSettings: StyledProps;
  loadingSettings: StyledProps;
  locationContentSettings: StyledProps;
  locationSelector: StyledProps;
  filters: StyledProps;
  destinationContentSettings: StyledProps;
  destinationSelector: StyledProps;
  searchContentSettings: StyledProps;
  search: StyledProps;
  errorLabel: TextProps;
  descriptionPaginationSettings: StyledProps;
  noResults: StyledProps;
  paginationContentSettings: StyledProps;
  pagination: StyledProps<$TSFixMe>;
  bookNowContentSettings: StyledProps;
  bookNowSettings: StyledProps;
  scroll: StyledProps;
  map: StyledProps;
  disclaimer: StyledProps;
  alert: StyledProps;
  getCorrectGeoIdFor?: 'OriginCity' | 'DestinationCity';
}>;

interface Props extends WithEMContainerProps {
  initialBusStations: $TSFixMe[];
  /**
   * Function to get BusStations
   */
  getBusStations: $TSFixMe;
  /**
   * Function to get Locations
   */
  getLocations: $TSFixMe;
  /**
   * Component Behavior when there are not results available
   */
  noStationsBehavior: NoFaresBehavior;
  /**
   * Prepopulation of the component
   */
  prePopulation: $TSFixMe;
  /**
   * Settings of the Pagination
   */
  paginationSettings: PaginationSettings;
  /**
   * Sub-Components settings
   */
  components: Components;
  /**
   * Visual settings of the components
   */
  uiDisplay: UIDisplay;
  /**
   * Title Label Key to translate
   */
  titleKey: string;
  flavor: 'cityMode' | 'routeMode' | 'stateMode' | 'stationMode';
  /**
   * Basic url for See More link
   */
  interlinkingBaseUrl: string;
  directionsBaseUrl: string;
  fareItemInteractionType: FareItemInteractionType;
  preferInterlinkingBaseUrl?: boolean;
  /**
   * Enable SEO in the component
   */
  enableSEO?: boolean;
}

export type BusDirectoryProps = StyledProps<Props>;

function BusDirectoryMapContainer({
  components = {},
  uiDisplay,
  prePopulation,
  ...props
}: BusDirectoryProps) {
  const {
    titleKey,
    translate,
    direction,
    noStationsBehavior,
    integrationSettings,
    paginationSettings,
    theme,
    enableSEO = true
  } = props;
  const { getCorrectGeoIdFor = '' } = components;
  const {
    isSkeletonShown,
    errorLoading,
    busStations,
    busStationSelected,
    originData,
    destinationData,
    origin,
    destination,
    selectedOrigin,
    selectedDestination,
    isLoadingOrigins,
    isLoadingDestinations,
    isLoading,
    points,
    errorOrigin,
    errorDestination,
    errorRoute,
    enableRoute,
    handleOnInputChange,
    inputOrigin,
    inputDestination,
    clearOrigin,
    handleOrigin,
    handleDestination,
    isPaginationAvailable,
    handlePaginationAction,
    reload,
    clearDestination,
    handleSearch,
    originSelectorRef,
    destinationSelectorRef,
    listItem,
    handleMarkerOnClick,
    handleTooltipOnClose,
    handleStationItemFocus,
    showNoData,
    getInterlinking,
    dispatch
  } = useBusDirectory({
    uiDisplay,
    prePopulation,
    getCorrectGeoIdFor,
    ...props
  } as $TSFixMe);

  const showDataSection = selectedOrigin || selectedDestination;

  const containerErrorAlertComponent = (
    <ContainerErrorAlert row={{ marginTop: '2' }} {...components.alert} tryAgain={reload} />
  );

  const headerComponent = (
    <Header
      tag="h2"
      fontSize="6"
      floatLeft
      textTransform="capitalize"
      content={translate(titleKey, 'FIND GREYHOUND STATIONS')}
      {...components.title}
    />
  );

  const interlinkingSection = uiDisplay.showInterlinkingSection ? (
    <StationsInterlink
      links={getInterlinking}
      marginTop="2"
      textAlign="center"
      {...components.interlinkingSettings}
    />
  ) : null;
  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);
  const itemSettings = components.list?.itemSettings ?? {};
  const onStationClick = (station: $TSFixMe, callToActionLabel: string) => {
    dispatch({ type: ActionType.STATION_SELECT, payload: { station, callToActionLabel } });
  };
  itemSettings.onFocus = handleStationItemFocus;
  itemSettings.onStationClick = onStationClick;

  const errorLabelKey = errorRoute
    ? 'cmp-bus-directory-map-route-error-input-selector'
    : 'cmp-bus-directory-map-error-input-selector';
  const handleBook = () => {
    dispatch({
      type: ActionType.FARE_CLICK,
      payload: {
        fare: {
          ...props,
          origin: selectedOrigin?.value,
          destination: selectedDestination?.value
        },
        callToActionLabel: translate('fare_call_to_action_label', 'Book now')
      }
    });
  };

  const handleZoomOnClick = (oldZoom: $TSFixMe, newZoom: $TSFixMe) => {
    dispatch({ type: ActionType.ZOOM, payload: { oldZoom, newZoom } });
  };

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  const renderContent = (
    <Box
      position="relative"
      style={myStyle}
      className={`${className} ${BusDirectoryMapContainer.componentName}`}
    >
      {isLoading && <Loading {...components.loadingSettings} />}
      {uiDisplay.showSkeletonScreen && isSkeletonShown && (
        <Skeleton {...components.skeletonSettings} position="absolute" width="100" />
      )}
      {uiDisplay.showTitle && titleKey && (
        <Row gutters>
          <Col>{headerComponent}</Col>
        </Row>
      )}
      {uiDisplay.showFilters && (
        <>
          <Row gutters {...components.filters} alignItems="end">
            {uiDisplay.showOrigin && (
              <Col lg={uiDisplay.showDestination ? 5 : 10} {...components.locationContentSettings}>
                <AutoComplete
                  placeholderKey="cmp-bus-directory-map-selector-placeholder"
                  labelKey="origin"
                  {...components.locationSelector}
                  options={originData}
                  value={inputOrigin === undefined ? origin && origin.value : inputOrigin}
                  onChange={handleOrigin}
                  onInputChange={(value: $TSFixMe) => handleOnInputChange(value, 'origin')}
                  hasError={errorOrigin}
                  inputFocusReferences={originSelectorRef}
                  dataTestId="originSelector"
                  onClear={clearOrigin}
                  loading={isLoadingOrigins}
                />
              </Col>
            )}
            {uiDisplay.showDestination && (
              <Col
                lg={uiDisplay.showOrigin ? 5 : 10}
                paddingTop="2"
                className="fr-pt-lg-0"
                {...components.destinationContentSettings}
              >
                <AutoComplete
                  placeholderKey="cmp-bus-directory-map-selector-placeholder"
                  labelKey="destination"
                  {...components.locationSelector}
                  {...components.destinationSelector}
                  options={destinationData}
                  value={
                    inputDestination === undefined
                      ? destination && destination.value
                      : inputDestination
                  }
                  onChange={handleDestination}
                  onInputChange={(value: $TSFixMe) => handleOnInputChange(value, 'destination')}
                  hasError={errorDestination || errorRoute}
                  inputFocusReferences={destinationSelectorRef}
                  dataTestId="destinationSelector"
                  onClear={clearDestination}
                  loading={isLoadingDestinations}
                />
              </Col>
            )}
            <Col lg={2} paddingTop="2" className="fr-pt-lg-0" {...components.searchContentSettings}>
              <Button
                rounded="@theme:button.rounded"
                color="primary"
                width="100"
                paddingTop="10px"
                paddingBottom="10px"
                {...components.search}
                onClick={handleSearch}
                cursor={isLoading ? 'default' : 'pointer'}
                disabled={isLoading}
              >
                {isLoading
                  ? translate('cmp-label-loading-with-dots', 'Loading...')
                  : translate('cmp-bus-directory-map-button-search', 'Search')}
              </Button>
            </Col>
          </Row>
          {(errorOrigin || errorDestination || errorRoute) && (
            <Text textColor="danger" {...components.errorLabel}>
              {translate(errorLabelKey, errorLabelKey)}
            </Text>
          )}
        </>
      )}
      {showDataSection && (
        <>
          {showNoData ? (
            <Row marginTop="2" gutters>
              {noStationsBehavior === 'showNoResults' && (
                <Col>
                  <NoResults onClick={reload} flavor="desktop" {...components.noResults} />
                </Col>
              )}
              {noStationsBehavior === 'hidden' && <NoFaresBehavior />}
            </Row>
          ) : (
            !errorLoading && (
              <Row marginTop="2" gutters>
                <Col lg={6}>
                  {uiDisplay.showPaginationResultsLabel && (
                    <PaginationDescription
                      origin={selectedOrigin?.label}
                      destination={selectedDestination?.label}
                      enableRoute={enableRoute}
                      resultsCount={points?.length}
                      {...components.descriptionPaginationSettings}
                    />
                  )}
                  <Box
                    {...components.scroll}
                    style={{
                      overflowY: 'auto',
                      maxHeight: '500px',
                      position: 'relative',
                      ...components.scroll?.style
                    }}
                  >
                    <List
                      oneColumn={true}
                      {...components.list}
                      style={{
                        alignContent: 'flex-start',
                        padding: '1px',
                        ...components.list?.style
                      }}
                      selectedItemSettings={{
                        borderColor: 'primary',
                        border: true,
                        ...components.list?.selectedItemSettings
                      }}
                      itemSettings={itemSettings}
                      focusOn={busStationSelected ? busStationSelected.index : null}
                      focusReferences={listItem}
                      ItemComponentInstance={StationItem}
                      itemsData={busStations}
                    />
                    {uiDisplay.showLoadMorePaginator && isPaginationAvailable() && (
                      <Box
                        textAlign="center"
                        marginTop="2"
                        marginBottom="3"
                        {...components.paginationContentSettings}
                      >
                        <LoadMorePaginator
                          isLoading={isLoading}
                          disabled={busStations.length >= (paginationSettings.limit as number)}
                          onClickAction={handlePaginationAction}
                          {...components.pagination}
                          buttonSetting={{
                            outline: true,
                            color: 'primary',
                            rounded: '@theme:button.rounded',
                            paddingWidth: '30px',
                            paddingHeight: '1',
                            ...components.pagination?.buttonSetting
                          }}
                        />
                      </Box>
                    )}
                    {uiDisplay.showBookNow && (
                      <Box textAlign="center" {...components.bookNowContentSettings}>
                        <Button
                          color="primary"
                          textTransform="uppercase"
                          rounded="@theme:button.rounded"
                          paddingWidth="30px"
                          paddingHeight="1"
                          marginTop="1"
                          onClick={handleBook}
                          {...components.bookNowSettings}
                        >
                          {translate('fare_call_to_action_label', 'Book now')}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Col>
                <Col lg={6}>
                  {!isSkeletonShown && (
                    <Map
                      heightContainer="530px"
                      {...components.map}
                      onClickZoom={handleZoomOnClick}
                      onClickMarker={handleMarkerOnClick}
                      onCloseTooltip={handleTooltipOnClose}
                      accessToken={integrationSettings.v5MapBoxApi.token}
                      enableRoute={enableRoute}
                      points={points}
                      errorFallback={() => containerErrorAlertComponent}
                      onStationClick={onStationClick}
                    />
                  )}
                </Col>
                {interlinkingSection && <Col lg={12}>{interlinkingSection}</Col>}
              </Row>
            )
          )}
          {errorLoading && containerErrorAlertComponent}
          {showDataSection && uiDisplay.showDisclaimer && (
            <Row gutters>
              <Col>
                <Disclaimer
                  marginTop="2"
                  marginBottom="2"
                  floatLeft
                  labelKey="cmp-bus-directory-map-disclaimer"
                  {...components.disclaimer}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </Box>
  );
  return renderContent;
}

export const defaultProps = {
  fareItemInteractionType: 'popup',
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareBusDirectoryMap'
  },
  uiDisplay: {
    showDisclaimer: true,
    showSkeletonScreen: true,
    showTitle: true,
    showBookNow: false,
    showOrigin: true,
    showDestination: false,
    showLoadMorePaginator: true,
    showInterlinkingSection: true,
    showPaginationResultsLabel: true,
    showFilters: true
  },
  titleKey: 'cmp-bus-directory-title-key',
  getBusStations: getBusStationsDefaultImpl,
  getLocations: getLocationsDefaultImpl,
  noStationsBehavior: 'showNoResults',
  prePopulation: busStationsDefaultPrePopulation,
  paginationSettings: {
    step: 10,
    limit: 30,
    initialVisibleItems: 10
  },
  flavor: '',
  interlinkingBaseUrl: null,
  directionsBaseUrl: 'https://www.google.com/maps/dir/Current+Location'
};

BusDirectoryMapContainer.defaultProps = defaultProps;
BusDirectoryMapContainer.componentName = 'BusDirectoryMapContainer';

BusDirectoryMapContainer.flavors = flavors;

export default withDeepMergeDefaultProps(BusDirectoryMapContainer);
